import {
  parseStoryFilmCatalog,
  parseStoryFilmFirstFreeCandidateCheck,
  parseStoryFilmFirstFreeCandidates,
  parseStoryFilmPrepareResult,
  parseStoryFilmRouteList,
  parseStoryFilmSeason,
} from '@drama/contracts/novel'

/**
 * Story 短剧（A 域）接口：来源准备、本人路线清单、季集目录与季管理。
 * 全部要求登录用户；他人的对局与季一律 NOT_FOUND。响应按 contracts 解析后返给调用方。
 * @param {{ request: Function }} transport
 * @param {{ prefix?: string }} [options]
 */
export function createStoryFilmApi(transport, { prefix = '/api/novel/story-film' } = {}) {
  const seasonPath = (season_id) => `${prefix}/seasons/${encodeURIComponent(season_id)}`
  return {
    /**
     * 唯一来源命令：手动准备、首免与自动拍共用。同键重放返回原结果（含 insufficient/stale）。
     * @param {{ run_id: string, branch_id?: string, through_turn_id?: string, request_key?: string }} body
     */
    prepare(body) {
      return transport.request('POST', `${prefix}/prepare`, { body, parse: parseStoryFilmPrepareResult })
    },
    /** @param {{ cursor?: string, limit?: number }} [query] */
    listRoutes({ cursor, limit } = {}) {
      const params = new URLSearchParams()
      if (cursor) params.set('cursor', cursor)
      if (limit !== undefined) params.set('limit', String(limit))
      const query = params.toString()
      return transport.request('GET', `${prefix}/routes${query ? `?${query}` : ''}`, { parse: parseStoryFilmRouteList })
    },
    /**
     * 只读：本人达到可拍条件的来源候选（B/C 首免受理竞争用；A 不锁定资格）。
     */
    getFirstFreeCandidates() {
      return transport.request('GET', `${prefix}/first-free/candidates`, { parse: parseStoryFilmFirstFreeCandidates })
    },
    /**
     * 只读：核对指定 run/branch 是否达标及在本人候选中的名次；B/C 受理事务内调用。
     * @param {{ run_id: string, branch_id: string, expected_source_version: string, expected_through_turn_id: string }} query
     */
    verifyFirstFreeCandidate({ run_id, branch_id, expected_source_version, expected_through_turn_id } = {}) {
      for (const [key, value] of Object.entries({ run_id, branch_id, expected_source_version, expected_through_turn_id })) {
        if (typeof value !== 'string' || value === '') throw new Error('story_film.verifyFirstFreeCandidate: ' + key + ' is required')
      }
      const params = new URLSearchParams({ run_id, branch_id, expected_source_version, expected_through_turn_id })
      return transport.request('GET', `${prefix}/first-free/candidate?${params.toString()}`, { parse: parseStoryFilmFirstFreeCandidateCheck })
    },
    /** @param {string} run_id @param {{ branch_id?: string }} [options] */
    getCatalog(run_id, { branch_id } = {}) {
      const params = new URLSearchParams({ run_id })
      if (branch_id) params.set('branch_id', branch_id)
      return transport.request('GET', `${prefix}/catalog?${params.toString()}`, { parse: parseStoryFilmCatalog })
    },
    /** @param {string} season_id @param {{ title: string, expected_updated_at?: string|null }} body */
    renameSeason(season_id, body) {
      return transport.request('PATCH', seasonPath(season_id), { body, parse: parseStoryFilmSeason })
    },
    /** @param {string} season_id @param {{ expected_updated_at?: string|null }} [body] */
    closeSeason(season_id, body = {}) {
      return transport.request('POST', `${seasonPath(season_id)}/close`, { body, parse: parseStoryFilmSeason })
    },
    /** @param {string} film_id 已在档期内的季未关闭时拒绝 */
    createNextSeason(film_id) {
      return transport.request('POST', `${prefix}/films/${encodeURIComponent(film_id)}/seasons`, { body: {}, parse: parseStoryFilmSeason })
    },
  }
}
